import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input, Label, Textarea, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, Briefcase, Building2, ChevronRight, ClipboardList, FileText, Plus, ShieldCheck, PencilLine, MapPin, Save, X, Loader2, Trash2 } from 'lucide-react';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { useAuth, hasPermission } from '@/lib/auth';

interface SiteCover {
  id: string;
  cover_number: string;
  status: 'draft' | 'issued';
  insured_name?: string;
  policy_period_start?: string;
  policy_period_end?: string;
  totals?: { total_sum_insured_zar?: number; total_premium_zar?: number };
}

const fmtZAR = (n?: number | null): string => (n == null || Number.isNaN(n)) ? '—' : `R\u00a0${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(iso).slice(0, 10); }
};

interface SiteInstruction {
  id: string;
  template_name?: string;
  template_version?: number;
  status?: string;
  issued_at?: string;
  due_date?: string | null;
  surveyor_email?: string | null;
  method?: string;
  bulk_batch_id?: string | null;
  public_link?: string;
  token?: string;
}

interface SiteDetail {
  id: string;
  name: string;
  site_reference?: string;
  physical_address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  place_id?: string | null;
  // Per-site geofence override — null falls back to Settings > Geolocation global.
  geofence_threshold_m?: number | null;
  site_contact_name?: string;
  site_contact_phone?: string;
  notes?: string;
  default_survey_template_id?: string | null;
  active?: boolean;
  client_id?: string;
  client_name?: string | null;
  broker_id?: string;
  broker_name?: string | null;
  broker_fsp?: string | null;
  instructions?: SiteInstruction[];
}

interface Template { id?: string; _id?: string; name?: string; current_published_version?: number | null }

const STATUS_VARIANT: Record<string, 'success' | 'muted' | 'destructive' | 'blue'> = {
  submitted: 'success', approved: 'success',
  issued: 'muted', in_progress: 'blue',
  rejected: 'destructive',
};

// Small labelled read-only cell used across the Site info card.
const Field: React.FC<{ label: string; value?: string | null; tabular?: boolean; full?: boolean }>
  = ({ label, value, tabular, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <div className="text-[10px] uppercase tracking-wide text-raptor-navy/50 font-body">{label}</div>
    <div className={`text-sm text-raptor-navy ${tabular ? 'font-tabular' : ''} ${value ? '' : 'italic text-raptor-navy/40'}`}>
      {value || '—'}
    </div>
  </div>
);

export const SiteDetailPage: React.FC = () => {
  const { siteId } = useParams();
  const nav = useNavigate();
  const { role } = useAuth();
  const canDelete = hasPermission(role, 'sites', 'delete');
  const delSite = async (): Promise<void> => {
    if (!siteId) return;
    if (!window.confirm('Delete this site? This cannot be undone.')) return;
    try {
      await api.delete(`/sites/${siteId}`);
      toast.success('Site deleted.');
      nav('/insurer/sites');
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not delete site.');
    }
  };
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [covers, setCovers] = useState<SiteCover[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<SiteDetail>>({});
  const [templates, setTemplates] = useState<Template[]>([]);

  const startEdit = (): void => {
    if (!site) return;
    setForm({
      name: site.name,
      site_reference: site.site_reference || '',
      physical_address: site.physical_address || '',
      city: site.city || '',
      province: site.province || '',
      postal_code: site.postal_code || '',
      latitude: site.latitude ?? undefined,
      longitude: site.longitude ?? undefined,
      place_id: site.place_id ?? undefined,
      geofence_threshold_m: site.geofence_threshold_m ?? null,
      site_contact_name: site.site_contact_name || '',
      site_contact_phone: site.site_contact_phone || '',
      notes: site.notes || '',
      default_survey_template_id: site.default_survey_template_id || '',
    });
    setEditing(true);
  };

  const saveEdit = async (): Promise<void> => {
    if (!siteId) return;
    setSaving(true);
    try {
      // Send only non-empty / defined fields; convert empty strings to null so the backend clears them.
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === undefined) continue;
        if (typeof v === 'string' && v.trim() === '') { payload[k] = null; continue; }
        payload[k] = v;
      }
      if (payload.latitude !== undefined && payload.latitude !== null) payload.latitude = Number(payload.latitude);
      if (payload.longitude !== undefined && payload.longitude !== null) payload.longitude = Number(payload.longitude);
      await api.patch(`/sites/${siteId}`, payload);
      const r = await api.get<SiteDetail>(`/sites/${siteId}`);
      setSite(r.data);
      setEditing(false);
      toast.success('Site updated');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(String(msg));
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get<SiteDetail>(`/sites/${siteId}`),
          api.get<SiteCover[]>(`/sites/${siteId}/covers`),
        ]);
        setSite(s.data);
        setCovers(c.data);
        api.get<Template[]>('/templates')
          .then((r) => setTemplates(r.data.filter((t) => t.current_published_version)))
          .catch(() => {});
      } catch (err) {
        toast.error('Failed to load site.');
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId]);

  if (loading) return <div className="text-raptor-navy/60 py-10 text-center">Loading…</div>;
  if (!site) return <div className="text-raptor-navy/60 py-10 text-center">Site not found.</div>;

  const instructions = site.instructions || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-detail-page">
      {/* Breadcrumb — full path Insurer → Broker → Client → Site */}
      <div className="flex items-center gap-1 text-xs font-body text-raptor-navy/60 flex-wrap">
        <button
          type="button"
          onClick={() => nav('/insurer/sites')}
          className="hover:text-raptor-blue inline-flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Sites
        </button>
        {site.broker_id && (
          <>
            <ChevronRight size={12} className="text-raptor-navy/30" />
            <button
              type="button"
              onClick={() => nav(`/insurer/brokers/${site.broker_id}`)}
              className="hover:text-raptor-blue"
            >{site.broker_name || site.broker_id}</button>
          </>
        )}
        {site.client_id && (
          <>
            <ChevronRight size={12} className="text-raptor-navy/30" />
            <button
              type="button"
              onClick={() => nav(`/insurer/clients/${site.client_id}`)}
              className="hover:text-raptor-blue"
            >{site.client_name || site.client_id}</button>
          </>
        )}
        <ChevronRight size={12} className="text-raptor-navy/30" />
        <span className="text-raptor-navy">{site.name}</span>
      </div>

      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">{site.name}</h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            {site.id}
            {site.site_reference && <> · Ref <span className="font-tabular">{site.site_reference}</span></>}
            {' '}
            {site.active === false ? <Badge variant="destructive">Inactive</Badge> : <Badge variant="success">Active</Badge>}
          </p>
        </div>
        {canDelete && (
          <Button size="sm" variant="outline" onClick={delSite} data-testid="delete-site-btn"
            className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 size={14} className="mr-1" /> Delete site
          </Button>
        )}
      </div>

      {/* Parent broker + client */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 size={16} className="text-raptor-blue" /> Broker
            </CardTitle>
          </CardHeader>
          <CardContent>
            {site.broker_id ? (
              <button
                type="button"
                onClick={() => nav(`/insurer/brokers/${site.broker_id}`)}
                className="w-full text-left rounded-md border border-border/60 p-3 hover:bg-raptor-neutral/30 flex items-center justify-between transition-colors"
                data-testid="site-broker-link"
              >
                <div>
                  <div className="font-semibold text-raptor-navy">{site.broker_name || site.broker_id}</div>
                  <div className="text-xs text-raptor-navy/60">
                    {site.broker_id}
                    {site.broker_fsp && <> · FSP {site.broker_fsp}</>}
                  </div>
                </div>
                <ExternalLink size={14} className="text-raptor-navy/40" />
              </button>
            ) : <div className="text-sm text-raptor-navy/50 italic">Not linked.</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase size={16} className="text-raptor-blue" /> Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            {site.client_id ? (
              <button
                type="button"
                onClick={() => nav(`/insurer/clients/${site.client_id}?tab=sites`)}
                className="w-full text-left rounded-md border border-border/60 p-3 hover:bg-raptor-neutral/30 flex items-center justify-between transition-colors"
                data-testid="site-client-link"
              >
                <div>
                  <div className="font-semibold text-raptor-navy">{site.client_name || site.client_id}</div>
                  <div className="text-xs text-raptor-navy/60">{site.client_id}</div>
                </div>
                <ExternalLink size={14} className="text-raptor-navy/40" />
              </button>
            ) : <div className="text-sm text-raptor-navy/50 italic">Not linked.</div>}
          </CardContent>
        </Card>
      </div>

      {/* --- Site information: view + inline edit --- */}
      <Card data-testid="site-info-card">
        <CardHeader className="flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin size={16} className="text-raptor-blue" /> Site information
            </CardTitle>
            <CardDescription>Address, GPS, on-site contact and internal notes.</CardDescription>
          </div>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={startEdit} data-testid="site-edit-btn">
              <PencilLine size={14} /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving} data-testid="site-edit-cancel">
                <X size={14} /> Cancel
              </Button>
              <Button size="sm" variant="gradient" onClick={() => void saveEdit()} disabled={saving} data-testid="site-edit-save">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save</>}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!editing ? (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm font-body">
              <Field label="Trading name" value={site.name} />
              <Field label="Site reference" value={site.site_reference} tabular />
              <Field label="Default survey template" value={templates.find((t) => (t.id || t._id) === site.default_survey_template_id)?.name || (site.default_survey_template_id || undefined)} />
              <Field label="Physical address" value={site.physical_address} full />
              <Field label="City / Town" value={site.city} />
              <Field label="Province" value={site.province} />
              <Field label="Postal code" value={site.postal_code} tabular />
              <Field label="GPS (lat, lng)" value={(site.latitude != null && site.longitude != null) ? `${site.latitude}, ${site.longitude}` : undefined} tabular />
              <Field label="Site contact name" value={site.site_contact_name} />
              <Field label="Site contact phone" value={site.site_contact_phone} tabular />
              <Field label="Notes" value={site.notes} full />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Trading name *</Label>
                <Input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-edit-name" />
              </div>
              <div className="sm:col-span-2">
                <Label>Site reference</Label>
                <Input value={form.site_reference ?? ''} onChange={(e) => setForm({ ...form, site_reference: e.target.value })} data-testid="site-edit-ref" />
              </div>
              <div className="sm:col-span-2">
                <Label>Default survey template</Label>
                <Select value={form.default_survey_template_id ?? ''} onChange={(e) => setForm({ ...form, default_survey_template_id: e.target.value })} data-testid="site-edit-default-template">
                  <option value="">— None (choose at issue) —</option>
                  {templates.map((t) => <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>)}
                </Select>
                <p className="text-[11px] text-raptor-navy/50 mt-1 font-body">Pre-selected when issuing or bulk-dispatching surveys for this site. You can still change it before sending.</p>
              </div>
              {/* ─── Location (address first, drives autofill) ── */}
              <div className="sm:col-span-2 pt-1 border-t border-border/40 mt-1">
                <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Location</div>
                <Label>Physical address</Label>
                <AddressAutocomplete
                  value={form.physical_address ?? ''}
                  onChange={(v: string) => setForm({ ...form, physical_address: v })}
                  onResolve={(p) => setForm((f) => ({
                    ...f,
                    physical_address: p.formatted_address,
                    city: p.city || f.city,
                    province: p.province || f.province,
                    postal_code: p.postal_code || f.postal_code,
                    latitude: p.lat, longitude: p.lng,
                    place_id: p.place_id,
                  }))}
                  placeholder="Start typing to auto-fill city, province, postal code and GPS…"
                  testid="site-edit-address"
                />
              </div>
              <div>
                <Label>City / Town</Label>
                <Input value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="site-edit-city" />
              </div>
              <div>
                <Label>Province</Label>
                <Input value={form.province ?? ''} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="site-edit-province" />
              </div>
              <div>
                <Label>Postal code</Label>
                <Input value={form.postal_code ?? ''} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="site-edit-postal" />
              </div>
              <div>
                <Label>Latitude</Label>
                <Input type="number" step="0.000001" value={form.latitude ?? ''} onChange={(e) => setForm({ ...form, latitude: e.target.value === '' ? undefined : Number(e.target.value) })} data-testid="site-edit-lat" />
              </div>
              <div className="sm:col-span-2">
                <Label>Longitude</Label>
                <Input type="number" step="0.000001" value={form.longitude ?? ''} onChange={(e) => setForm({ ...form, longitude: e.target.value === '' ? undefined : Number(e.target.value) })} data-testid="site-edit-lng" />
              </div>
              <div>
                <Label>Site contact name</Label>
                <Input value={form.site_contact_name ?? ''} onChange={(e) => setForm({ ...form, site_contact_name: e.target.value })} data-testid="site-edit-contact-name" />
              </div>
              <div>
                <Label>Site contact phone</Label>
                <Input value={form.site_contact_phone ?? ''} onChange={(e) => setForm({ ...form, site_contact_phone: e.target.value })} placeholder="+27…" data-testid="site-edit-contact-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label>Internal notes</Label>
                <Textarea rows={3} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="site-edit-notes" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Confirmations of Cover for this site */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText size={16} className="text-raptor-blue" /> Confirmations of Cover · {covers.length}
            </CardTitle>
            <CardDescription>Underwriting artefacts bound to this site.</CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => nav(`/insurer/covers/new?client_id=${site.client_id || ''}&site_id=${site.id}`)}
            data-testid="site-add-cover"
          >
            <Plus size={14} /> Draft new cover
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {covers.length === 0 ? (
            <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">No covers for this site yet.</div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3 text-left">Cover №</th>
                  <th className="py-2 px-3 text-left">Insured</th>
                  <th className="py-2 px-3 text-left">Period</th>
                  <th className="py-2 px-3 text-right">Sum insured</th>
                  <th className="py-2 px-3 text-right">Premium</th>
                  <th className="py-2 px-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {covers.map((c) => (
                  <tr key={c.id} onClick={() => nav(`/insurer/covers/${c.id}`)} className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer" data-testid={`site-cover-row-${c.id}`}>
                    <td className="py-2 px-3 font-tabular font-semibold text-raptor-navy">{c.cover_number}</td>
                    <td className="py-2 px-3">{c.insured_name || '—'}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/70">{fmtDate(c.policy_period_start)} → {fmtDate(c.policy_period_end)}</td>
                    <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_sum_insured_zar)}</td>
                    <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_premium_zar)}</td>
                    <td className="py-2 px-3">
                      {c.status === 'issued' ? (
                        <Badge variant="success"><ShieldCheck size={10} /> Issued</Badge>
                      ) : (
                        <Badge variant="warning"><PencilLine size={10} /> Draft</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Survey instructions history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList size={16} className="text-raptor-blue" /> Survey history · {instructions.length}
          </CardTitle>
          <CardDescription>Every survey dispatched to this site — newest first.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {instructions.length === 0 ? (
            <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">
              No surveys dispatched to this site yet.
            </div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3 text-left">Issued</th>
                  <th className="py-2 px-3 text-left">Template</th>
                  <th className="py-2 px-3 text-left">Method</th>
                  <th className="py-2 px-3 text-left">Due</th>
                  <th className="py-2 px-3 text-left">Status</th>
                  <th className="py-2 px-3 text-left">Batch</th>
                </tr>
              </thead>
              <tbody>
                {instructions.map((i) => (
                  <tr key={i.id} className="border-t border-border/60">
                    <td className="py-2 px-3 font-tabular text-xs">{i.issued_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-2 px-3">
                      {i.template_name}
                      {i.template_version && <span className="text-xs text-raptor-navy/50"> · v{i.template_version}</span>}
                    </td>
                    <td className="py-2 px-3 text-xs">{i.method === 'on_site' ? 'On-site' : 'Remote'}</td>
                    <td className="py-2 px-3 text-xs">{i.due_date || '—'}</td>
                    <td className="py-2 px-3">
                      <Badge variant={STATUS_VARIANT[i.status || 'issued'] || 'muted'}>{i.status || 'issued'}</Badge>
                    </td>
                    <td className="py-2 px-3 font-tabular text-[10px] text-raptor-navy/60">{i.bulk_batch_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Location + contact details */}
      <Card>
        <CardHeader><CardTitle className="text-base">Location &amp; contact</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid md:grid-cols-2 gap-3 text-sm font-body">
            <div className="md:col-span-2"><dt className="text-raptor-navy/60 text-xs">Address</dt><dd>{site.physical_address || '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">City</dt><dd>{site.city || '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">Province</dt><dd>{site.province || '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">Postal code</dt><dd>{site.postal_code || '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">Coordinates</dt><dd>{site.latitude != null && site.longitude != null ? `${site.latitude}, ${site.longitude}` : '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">Site contact</dt><dd>{site.site_contact_name || '—'}</dd></div>
            <div><dt className="text-raptor-navy/60 text-xs">Contact phone</dt><dd>{site.site_contact_phone || '—'}</dd></div>
            {site.notes && (
              <div className="md:col-span-2"><dt className="text-raptor-navy/60 text-xs">Notes</dt><dd className="whitespace-pre-wrap">{site.notes}</dd></div>
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteDetailPage;
